import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, Users, CheckCircle2, Gift, Star, ChevronDown, ChevronUp, PackageCheck } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function AdminReferrals() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/referrals");
        setData(data);
      } catch (err) {
        toast.error(apiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (user && user.role !== "admin") {
    return <div className="p-14 text-red-400 uppercase text-sm">Admin only.</div>;
  }

  const leaders = data?.leaders || [];
  const totals = data?.totals || { referrers: 0, referred: 0, verified: 0 };

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-5xl mx-auto" data-testid="admin-referrals-page">
      <div className="mb-8">
        <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Admin</span>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">
          Referral leaderboard
        </h1>
        <p className="text-sm text-zinc-500 mt-3">
          Who brings in the best haulers — ranked by verified recruits, with the hauls and ratings their drivers go on to earn.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] mb-8">
        <Tile label="Active referrers" value={totals.referrers} icon={Trophy} accent="text-[#FF3B30]" testid="tile-referrers" />
        <Tile label="Drivers invited" value={totals.referred} icon={Users} accent="text-zinc-300" testid="tile-invited" />
        <Tile label="Recruits verified" value={totals.verified} icon={CheckCircle2} accent="text-emerald-400" testid="tile-verified" />
      </div>

      {loading ? (
        <div className="text-zinc-500 text-xs uppercase tracking-widest animate-pulse">Loading leaderboard…</div>
      ) : leaders.length === 0 ? (
        <div className="border border-dashed border-white/[0.08] p-12 text-center bg-[#0F0F10]" data-testid="referrals-empty">
          <div className="font-display text-2xl font-black uppercase">No referrals yet</div>
          <p className="text-zinc-500 text-sm mt-2">When drivers share their link and recruits sign up, the leaderboard appears here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {leaders.map((l, idx) => (
            <div key={l.referrer_id} className="border border-white/[0.08] bg-[#0F0F10] p-5" data-testid={`ref-leader-${l.referrer_id}`}>
              <button
                type="button"
                onClick={() => setExpanded(expanded === l.referrer_id ? null : l.referrer_id)}
                data-testid={`ref-expand-${l.referrer_id}`}
                className="w-full text-left"
              >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-4 min-w-0">
                    <div
                      className={`font-display text-3xl font-black w-12 text-center ${
                        idx === 0 ? "text-[#FF3B30]" : "text-zinc-600"
                      }`}
                      data-testid={`ref-rank-${l.referrer_id}`}
                    >
                      #{idx + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-xl font-black uppercase truncate">{l.referrer_name}</div>
                      <div className="text-xs text-zinc-500 truncate">{l.referrer_email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-5 flex-wrap text-center">
                    <Stat label="Invited" value={l.total_referred} testid={`ref-invited-${l.referrer_id}`} />
                    <Stat label="Verified" value={l.verified_referred} accent="text-emerald-400" testid={`ref-verified-${l.referrer_id}`} />
                    <Stat label="Bonus mo." value={l.bonus_months_granted} accent="text-[#FF3B30]" testid={`ref-bonus-${l.referrer_id}`} />
                    <Stat label="Hauls" value={l.referred_hauls_completed} testid={`ref-hauls-${l.referrer_id}`} />
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold">Avg rating</div>
                      <div className="font-display text-xl font-black flex items-center gap-1 justify-center" data-testid={`ref-rating-${l.referrer_id}`}>
                        {l.referred_rating_avg != null ? (
                          <>
                            <Star className="w-3.5 h-3.5 text-[#FF3B30]" fill="#FF3B30" />
                            {l.referred_rating_avg.toFixed(1)}
                          </>
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </div>
                    </div>
                    {expanded === l.referrer_id ? (
                      <ChevronUp className="w-4 h-4 text-[#FF3B30]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-zinc-500" />
                    )}
                  </div>
                </div>
              </button>
              {expanded === l.referrer_id && (
                <div className="mt-4 border-t border-white/[0.06] pt-4" data-testid={`ref-recruits-${l.referrer_id}`}>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2 flex items-center gap-2">
                    <PackageCheck className="w-3.5 h-3.5" /> Recruited via code {l.referral_code}
                  </div>
                  <div className="space-y-1">
                    {l.referred.map((r) => (
                      <div
                        key={r.id}
                        className="flex items-center justify-between gap-3 text-sm py-1.5 border-b border-white/[0.04] last:border-0"
                        data-testid={`ref-recruit-${r.id}`}
                      >
                        <span className="truncate">
                          <span className="font-bold">{r.name}</span>
                          <span className="text-zinc-500 text-xs ml-2">{r.email}</span>
                        </span>
                        <span className="text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">
                          {r.role} · joined {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-zinc-600 mt-6 flex items-center gap-2">
        <Gift className="w-3.5 h-3.5" />
        Bonus months are granted automatically when a referred driver completes KYC verification.
      </p>
    </div>
  );
}

function Tile({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="bg-[#0F0F10] p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">{label}</div>
        <Icon className={`w-4 h-4 ${accent}`} />
      </div>
      <div className="font-display text-5xl font-black uppercase mt-3 leading-none">{value}</div>
    </div>
  );
}

function Stat({ label, value, accent = "text-white", testid }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold">{label}</div>
      <div className={`font-display text-xl font-black ${accent}`} data-testid={testid}>{value}</div>
    </div>
  );
}
